/**
 * Das Schnapsroulette.
 *
 * Die Reihenfolge ist entscheidend: erst zieht der Server und persistiert,
 * dann dreht das Rad — die Animation läuft dem Ergebnis nur hinterher. Ein
 * Reload ändert nichts mehr, das Rad ist reine Inszenierung.
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import type { Schnaps } from '../api/typen.js'

const FARBEN = ['#241416', '#34211a']

/** So lange laeuft das Rad aus. */
const LAUF_MS = 4200

interface RadProps {
  kandidaten: Schnaps[]
  gezogenIndex: number
}

/**
 * Steht das Rad?
 *
 * Der Zeitgeber gehört bewusst dem Aufrufer und nicht dem Rad: der
 * Ereignisstrom lädt die Sitzung ständig neu, und läge die Uhr im Rad, würde
 * sie bei jedem Neuzeichnen von vorn beginnen. Dann steht das Rad nie still,
 * das Ergebnis erscheint nie — und niemand kommt vom Rad wieder weg.
 */
export function useRadSteht(aktiv: boolean): boolean {
  const [steht, setSteht] = useState(false)
  const reduziert = useMemo(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  )

  useEffect(() => {
    if (!aktiv) {
      setSteht(false)
      return
    }
    // Ohne Bewegung: sofort das Ergebnis, keine Inszenierung.
    if (reduziert) {
      setSteht(true)
      return
    }
    const ende = setTimeout(() => setSteht(true), LAUF_MS)
    return () => clearTimeout(ende)
  }, [aktiv, reduziert])

  return steht
}

export function Gluecksrad({ kandidaten, gezogenIndex }: RadProps) {
  const [dreht, setDreht] = useState(false)

  const segmentWinkel = 360 / kandidaten.length
  // Fünf volle Umdrehungen, dann exakt auf der Mitte des gezogenen Segments
  // unter dem Zeiger (oben) auslaufen.
  const zielRotation = 5 * 360 + (360 - (gezogenIndex * segmentWinkel + segmentWinkel / 2))

  const reduziert = useMemo(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  )

  useEffect(() => {
    if (reduziert) return
    const anlauf = requestAnimationFrame(() => setDreht(true))
    return () => cancelAnimationFrame(anlauf)
  }, [reduziert])

  const groesse = 260
  const radius = groesse / 2
  const nabenRadius = 28

  return (
    <div className="relative mx-auto" style={{ width: groesse, height: groesse }}>
      {/* Der Zeiger der Kammer. */}
      <div
        aria-hidden="true"
        className="absolute -top-1 left-1/2 z-10 -translate-x-1/2"
        style={{
          width: 0,
          height: 0,
          borderLeft: '10px solid transparent',
          borderRight: '10px solid transparent',
          borderTop: '16px solid #c89b3c',
        }}
      />
      <svg
        viewBox={`0 0 ${groesse} ${groesse}`}
        width={groesse}
        height={groesse}
        role="img"
        aria-label="Schnapsroulette"
        style={
          {
            transform: `rotate(${dreht || reduziert ? zielRotation : 0}deg)`,
            transition: reduziert ? 'none' : `transform ${LAUF_MS - 200}ms cubic-bezier(0.15, 0.9, 0.28, 1)`,
          } as CSSProperties
        }
      >
        {kandidaten.map((sorte, i) => {
          const von = ((i * segmentWinkel - 90) * Math.PI) / 180
          const bis = (((i + 1) * segmentWinkel - 90) * Math.PI) / 180
          const gross = segmentWinkel > 180 ? 1 : 0
          const pfad = [
            `M ${radius} ${radius}`,
            `L ${radius + radius * Math.cos(von)} ${radius + radius * Math.sin(von)}`,
            `A ${radius} ${radius} 0 ${gross} 1 ${radius + radius * Math.cos(bis)} ${radius + radius * Math.sin(bis)}`,
            'Z',
          ].join(' ')
          const mitte = ((i + 0.5) * segmentWinkel - 90) * (Math.PI / 180)
          const textRadius = radius * 0.72

          return (
            <g key={sorte.id}>
              <path
                d={pfad}
                fill={FARBEN[i % FARBEN.length]}
                stroke="#c89b3c"
                strokeWidth="0.75"
                strokeOpacity="0.5"
              />
              <text
                x={radius + textRadius * Math.cos(mitte)}
                y={radius + textRadius * Math.sin(mitte)}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize="16"
              >
                {sorte.emoji}
              </text>
            </g>
          )
        })}
        <circle cx={radius} cy={radius} r={nabenRadius} fill="#150e0f" stroke="#c89b3c" />
        <text
          x={radius}
          y={radius}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="18"
        >
          ⚖️
        </text>
      </svg>
    </div>
  )
}
